import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import Button from '@/components/common/Button';
import SectionPanel from '@/components/order/Share/SectionPanel';
import BuyerList from '@/components/order/Share/BuyerList';
import NoReceiver from '@/components/order/Share/NoReceiver';
import WaitLoading from '../WaitLoading';
import { orderShareClaim, getOrderWeeebatesLandingBanner } from '@/api/growth';
import Toast from '@/components/common/Toast';
import Trans from 'next-translate/Trans';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getZipcode } from '@/utils/common/porder';
import { isSayWeeeApp } from '@/utils/weeeapp';
import { buildUrlWithOrigin } from '@/utils/url';
import { isAndroid, isChrome, isMobileBrowser, isSafari, isWeixinBrowser } from '@/utils/common/device';
import { CroppedImage } from '@weee-fe/module';
import callAppFunction from '@/utils/bridge';

type OrderDetailProps = {
  data: any;
  id: string;
  isLogin: boolean;
  isAutoReceive: boolean; //自动领取
};

// 下载app提示 safari & 安卓微信不显示
export const showOpenAppDevice = () => {
  return isMobileBrowser() && !isSayWeeeApp() && !(isSafari() && !isChrome()) && !(isWeixinBrowser() && isAndroid());
};

const GroceryLandingControl: React.FC<OrderDetailProps> = props => {
  const { data, id, isLogin, isAutoReceive } = props;
  const { creator, receivers, orderShareProducts, orderShare, vendor } = data;

  const { t, lang } = useTranslation('common');
  const { buttonClick } = useClickActionT2();
  const router = useRouter();
  const redirectUrl = buildUrlWithOrigin(
    `/${lang}/weeebates/share/grocery/landing/${+router?.query?.id}?status=fromLogged`
  );

  const allowClick = useRef<boolean>(true);
  const [visibleWaitLoading, setVisibleWaitLoading] = useState(!!isAutoReceive);
  const [landingImage, setLandingImage] = useState<string>('');

  // 判断分享是否过期
  const isExpired = orderShare?.url_validity_period === false;

  const goLogin = () => {
    window.location.href = `/account/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGetDiscount = async (type?) => {
    if (!allowClick) return;
    allowClick.current = false;
    if (!isLogin) {
      router.push(`/${lang}/order/share/grocery/sign-up?id=${id}`);
      return;
    }
    setVisibleWaitLoading(true);
    const res = await orderShareClaim({
      orderId: id,
      zipcode: getZipcode()
    });

    if (res?.result) {
      router.push(`/${lang}/order/share/grocery/follow-buy/${id}`);
    } else {
      setVisibleWaitLoading(false);
      if (res?.message_id === 'PEN0001') {
        goLogin();
        return;
      }
      Toast.info(res?.message);
    }
    setTimeout(() => {
      allowClick.current = true;
    }, 200);
  };

  const trackClick = (type?) => {
    buttonClick({
      co: {
        target_nm: type ? type : 'claim_discount_now',
        target_type: 'normal_button',
        click_type: 'view'
      }
    });
  };

  useEffect(() => {
    if (!!isAutoReceive) {
      handleGetDiscount('auto');
    }
  }, [isAutoReceive]);

  // 获取landing_image的逻辑
  useEffect(() => {
    if (!orderShareProducts?.length && !isAutoReceive) {
      getOrderWeeebatesLandingBanner({ orderId: id })
        .then(res => {
          if (res?.result && res?.object?.landing_image) {
            setLandingImage(res.object.landing_image);
          }
        })
        .catch(error => {
          console.error('Failed to fetch landing image:', error);
        });
    }
  }, [orderShareProducts, isAutoReceive, id]);

  return (
    <div className="relative">
      {visibleWaitLoading && <WaitLoading />}

      <div className="bg-surface-100-bg p-5 text-center">
        <div className="mb-10 max-w-[670px] mx-auto enki-heading-xl text-primary-1">
          <Trans
            i18nKey="common:order_share_grocery_landing_title"
            components={{
              span: <span className="text-[36px] font-bold" />,
              p: <p className="mt-3 text-sm text-surface-100-fg-minor" />
            }}
            values={{
              discount: data?.maxDiscount,
              name: creator?.alias
            }}
          />
        </div>

        <div className="-mx-5 mb-5">
          {!!orderShareProducts?.length ? (
            // 显示产品图片列表
            <div className="max-w-[400px] mx-auto px-5 box-border flex flex-wrap gap-1.5 justify-center">
              {orderShareProducts?.map((item, index) => {
                if (index >= 15) return null;
                return (
                  <CroppedImage
                    key={`${index}-${item?.id}`}
                    url={item?.product_image_url || ''}
                    configs={{
                      renderWidth: 80,
                      renderHeight: 80
                    }}
                    wrapperClassName="w-20 h-20 rounded-lg border border-surface-100-hairline border-solid box-border overflow-hidden"
                    className="w-full h-full object-cover"
                    alt={`weee product ${item?.name}`}
                  />
                );
              })}
            </div>
          ) : (
            // 显示landing_image
            <CroppedImage
              url={landingImage || ''}
              configs={{
                renderWidth: 400
              }}
              placeholder={
                <div className="px-5">
                  <div className="h-[80px] max-w-[400px] mx-auto skeleton rounded-lg" />
                </div>
              }
              wrapperClassName="min-h-[80px] max-w-[400px] mx-auto overflow-hidden"
              className="w-full h-auto object-cover"
              alt="weee landing image"
            />
          )}
        </div>

        <div className="px-5 py-3 max-w-[400px] mx-auto">
          {isExpired ? (
            <>
              <Button type="secondary" disabled className="mb-2">
                {t('offer_expired')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  window.location.href = '/';
                }}>
                {t('continue_shopping')}
              </Button>
            </>
          ) : (
            <Button
              type="secondary"
              onClick={() => {
                trackClick('');
                handleGetDiscount();
              }}>
              {t('order_share_grocery_landing_btn_claim')}
            </Button>
          )}
          {vendor?.vendor_id && (
            <div className="mt-2 enki-body-2xs-medium text-surface-100-fg-minor">
              <Trans
                i18nKey="orderShare:order_share_onselective_items"
                values={{
                  store_name: vendor?.title
                }}
                components={{
                  a: <a className="text-link-base-1" href={vendor.seller_url} />
                }}
              />
            </div>
          )}
          {!isLogin && (
            <div className="mt-6 enki-body-sm-medium text-surface-100-fg-default">
              <Trans
                i18nKey="common:order_share_grocery_landing_anonymous_tips"
                components={{
                  link: <a className="text-link-base-1" onClick={goLogin} />,
                  br: <br />
                }}
                values={{
                  discount: `${data?.maxDiscount}`
                }}
              />
            </div>
          )}
        </div>
      </div>

      <SectionPanel title={t('order_share_grocery_landing_get_list_title')}>
        <>
          {!!receivers?.length ? (
            <BuyerList data={receivers} showPoints={false} showAnonymousUser hideAward />
          ) : (
            <NoReceiver />
          )}
        </>
      </SectionPanel>
    </div>
  );
};
export default GroceryLandingControl;
